import React from "react";
import { InputAdornment, ToggleButton, ToggleButtonGroup } from "@mui/material";

// A search box's scope switch ("All | Code" on /products, "All | Barcode" at the
// till), drawn exactly as the desktop till draws its scan bar's ALL | BARCODE
// (erp.desktop App.axaml `Border.posscanmode` / `Button.posscanseg`): a quiet track
// inside the right end of the box, the chosen half a raised white pill in accent ink
// and SemiBold — told apart by fill, weight AND ink, never colour alone. Same numbers
// as the till's tokens (track 28px / 3px padding, segments 22px / 10px sides / 12px
// text), with rounder corners than the till's 4 / 3 at the owner's request.
//
// Render it as the field's endAdornment, and give the field 6px of right padding:
// a 40px box around a 28px track leaves 6px above and below, and the right edge gets
// the same 6px so the track sits evenly inset. The track itself carries no margin.
// Shared by the list search (DynamicTable/DeepSearch) and the POS scan box so the two
// cannot drift.
export const SEARCH_MODE_FIELD_PADDING_RIGHT = "6px";

// The till's ShadowCard (#0A000000 / #0F000000 = 4% / 6% black).
const SHADOW_CARD = "0 2px 4px rgba(0,0,0,0.04), 0 8px 16px rgba(0,0,0,0.06)";
// The till's AccentText is its accent darkened for small text (#5765B0 against
// #6271BD = 89%). Derived from --primary-rgb rather than hardcoded, so a tenant
// brand colour still carries through.
const ACCENT_TEXT = "color-mix(in srgb, rgb(var(--primary-rgb)) 89%, #000)";

const trackSx = {
  height: "28px",
  p: "3px",
  mr: 0,
  gap: 0,
  // Concentric with the pill inside: 6px pill + 3px track padding ≈ 8px.
  borderRadius: "8px",
  bgcolor: "#F3F3F5", // ControlFillDisabled
  "& .MuiToggleButton-root": {
    height: "22px",
    minHeight: "22px",
    px: "10px",
    py: 0,
    m: 0,
    border: 0,
    borderRadius: "6px !important",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1,
    letterSpacing: 0,
    textTransform: "none",
    color: "#616166", // TextSecondary
    bgcolor: "transparent",
    "&:hover": { bgcolor: "transparent", color: "#1B1B1F" }, // TextPrimary
  },
  "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover":
    {
      bgcolor: "#FFFFFF", // ControlFill
      boxShadow: SHADOW_CARD,
      color: ACCENT_TEXT,
      fontWeight: 600,
    },
};

const SearchModeSegment = ({ modes, value, onChange, title }) => (
  <InputAdornment position="end" sx={{ ml: "8px", mr: 0, maxHeight: "none" }}>
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      title={title}
      // A null value is a click on the already-selected button; one scope must
      // always be active.
      onChange={(_, next) => next && onChange(next)}
      sx={trackSx}
    >
      {modes.map((m) => (
        <ToggleButton
          key={m.value}
          value={m.value}
          disableRipple
          // Not focusable in effect, like the till's segments: the caret stays in the
          // box, so a cashier who clicks the switch and then scans does not have the
          // scan land on a button.
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
        >
          {m.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </InputAdornment>
);

export default SearchModeSegment;
